import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

// Save a generated jsPDF document. On web it downloads; on native (Android/iOS)
// it writes the file and opens the share sheet — same approach as the employee
// payslip download, which works on mobile.
async function savePdfDocument(doc, fileName, shareTitle) {
  if (!Capacitor.isNativePlatform()) {
    doc.save(fileName);
    return;
  }

  const pdfBase64 = doc.output('datauristring').split(',')[1];

  await Filesystem.writeFile({
    path: fileName,
    data: pdfBase64,
    directory: Directory.Documents,
  });

  const uriResult = await Filesystem.getUri({
    directory: Directory.Documents,
    path: fileName,
  });

  await Share.share({
    title: shareTitle,
    text: 'Your PDF is ready.',
    url: uriResult.uri,
    dialogTitle: shareTitle,
  });
}

const PRIMARY = [49, 148, 160]; // #3194A0
const DARK = [30, 41, 59];
const MUTED = [100, 116, 139];
const LIGHT_BG = [241, 245, 249];

function calculateWeightedScore(question, section, starScore) {
  if (!section?.weight || !section?.questions?.length) return 0;

  const questionsInSection = section.questions.length;
  const questionWeight = section.weight / questionsInSection;  // Weight per question
  const maxStars = 5;

  return (starScore / maxStars) * questionWeight;
}

export async function generateAppraisalPDF(report) {
  const { appraisal, template, employee, cycle } = report;
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  let y = 15;

  const addPage = () => { doc.addPage(); y = 15; };
  const checkSpace = (needed) => { if (y + needed > 275) addPage(); };

  // ── HEADER ──
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageW, 38, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Performance Appraisal Report', 14, 16);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(cycle?.name || appraisal.cycleName || 'Appraisal', 14, 24);

  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, 14, 31);

  // Status badge
  const statusText = appraisal.status;
  const statusW = doc.getTextWidth(statusText) + 8;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(pageW - 14 - statusW, 12, statusW, 8, 2, 2, 'F');
  doc.setTextColor(...PRIMARY);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(statusText, pageW - 14 - statusW + 4, 17.5);

  y = 48;

  // ── EMPLOYEE INFO ──
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(14, y, pageW - 28, 30, 3, 3, 'F');

  doc.setTextColor(...DARK);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(employee?.name || appraisal.employeeName, 20, y + 10);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  doc.text(employee?.designation || '', 20, y + 17);

  const infoCol2 = 100;
  doc.setFontSize(8);
  doc.text(`ID: ${appraisal.employeeId}`, infoCol2, y + 8);
  doc.text(`Department: ${employee?.department || appraisal.department}`, infoCol2, y + 14);
  doc.text(`Manager: ${employee?.manager || '—'}`, infoCol2, y + 20);
  doc.text(`Joined: ${employee?.joinDate || '—'}`, infoCol2, y + 26);

  y += 38;

  // ── FINAL RATING (if completed) ──
  if (appraisal.status === 'Completed' && appraisal.hrSignOff?.finalRating) {
    doc.setFillColor(220, 252, 231);
    doc.roundedRect(14, y, pageW - 28, 18, 3, 3, 'F');
    doc.setDrawColor(34, 197, 94);
    doc.roundedRect(14, y, pageW - 28, 18, 3, 3, 'S');

    doc.setTextColor(21, 128, 61);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Final Rating: ${appraisal.hrSignOff.finalRating}/5`, 20, y + 8);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Signed by: ${appraisal.hrSignOff.signedBy || '—'}  |  Date: ${(appraisal.hrSignOff.signedAt || '').slice(0, 10)}`, 20, y + 14);
    y += 24;
  }

  // ── SECTIONS WITH QUESTIONS ──
  const sections = template?.sections || [];
const selfResponses = appraisal.selfReview?.responses || [];
const hrResponses = appraisal.hrReview?.responses || appraisal.approvalReview?.responses || []
const adminResponses = appraisal.adminReview?.responses || [];

let totalSelfScore = 0;
let totalHrScore = 0;
let totalMaxWeight = 0;


  const findResp = (responses, qId) => {
    return responses.find(r => r.question_id === qId || r.questionId === qId) || {};
  };

  const addSectionComments = (heading, questions, responses) => {
    const sectionComments = questions
      .map((q, qi) => ({ label: `Q${qi + 1}`, comment: findResp(responses, q.id).comment }))
      .filter(c => c.comment);

    if (sectionComments.length === 0) return;

    checkSpace(8 + sectionComments.length * 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...MUTED);
    doc.text(heading, 16, y);
    y += 4;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    for (const c of sectionComments) {
      const lines = doc.splitTextToSize(`${c.label}: "${c.comment}"`, pageW - 40);
      checkSpace(lines.length * 3.5 + 2);
      doc.text(lines, 20, y);
      y += lines.length * 3.5 + 2;
    }
  };

  const addFinalComment = (heading, comment) => {
    if (!comment) return;

    checkSpace(30);
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(14, y, pageW - 28, 6, 2, 2, 'F');
    doc.setTextColor(...DARK);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(heading, 18, y + 4.5);
    y += 10;

    doc.setTextColor(...DARK);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    for (const line of doc.splitTextToSize(comment, pageW - 40)) {
      checkSpace(4);
      doc.text(line, 18, y);
      y += 4;
    }
    y += 6;
  };

  for (const section of sections) {
    checkSpace(20);

    // Section header
    doc.setFillColor(...PRIMARY);
    doc.roundedRect(14, y, pageW - 28, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(section.title, 18, y + 7);
    if (section.weight > 0) {
      const wt = `Weight: ${section.weight}%`;
      doc.setFontSize(8);
      doc.text(wt, pageW - 18 - doc.getTextWidth(wt), y + 7);
    }
    y += 14;

    let sectionSelfTotal = 0;
    let sectionHrTotal = 0;

    // Questions table
    const questions = section.questions || [];
    if (questions.length > 0) {
      const tableData = questions.map((q, qi) => {
        const selfR = findResp(selfResponses, q.id);
        const hrR = findResp(hrResponses, q.id);

        const selfStars = selfR.star_score || selfR.starScore || selfR.rating || 0;
        const hrStars = hrR.star_score || hrR.starScore || hrR.rating || 0;

        const selfWeightedScore = calculateWeightedScore(q, section, selfStars);
        const hrWeightedScore = calculateWeightedScore(q, section, hrStars);

        sectionSelfTotal += selfWeightedScore;
        sectionHrTotal += hrWeightedScore;

        return [
          `Q${qi + 1}`,
          q.text,
          selfR.rating_label || '—',
          selfStars > 0 ? `${selfStars}/5 (${selfWeightedScore.toFixed(1)} pts)` : '—',
          hrR.rating_label || '—',
          hrStars > 0 ? `${hrStars}/5 (${hrWeightedScore.toFixed(1)} pts)` : '—',
        ];
      });

      checkSpace(10 + questions.length * 12);

      autoTable(doc, {
        startY: y,
        margin: { left: 14, right: 14 },
        head: [['#', 'Question', 'Self Rating', 'Self Score', 'Reviewer Rating', 'Reviewer Score']],
        body: tableData,
        headStyles: {
          fillColor: LIGHT_BG,
          textColor: DARK,
          fontStyle: 'bold',
          fontSize: 7.5,
          cellPadding: 2.5,
        },
        bodyStyles: {
          fontSize: 7.5,
          textColor: DARK,
          cellPadding: 2.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center', fontStyle: 'bold', textColor: MUTED },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 30 },
          3: { cellWidth: 18, halign: 'center' },
          4: { cellWidth: 30 },
          5: { cellWidth: 18, halign: 'center' },
        },
        theme: 'grid',
        styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
      });

      y = doc.lastAutoTable?.finalY || y + questions.length * 10 + 10;
      y += 4;

      // Section subtotal
      if (section.weight > 0) {
        checkSpace(8);
        doc.setFillColor(...LIGHT_BG);
        doc.roundedRect(14, y, pageW - 28, 7, 1.5, 1.5, 'F');
        doc.setTextColor(...DARK);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.text('Section Total', 18, y + 5);
        const subtotalText = `Self: ${sectionSelfTotal.toFixed(1)} / ${section.weight}   |   Reviewer: ${sectionHrTotal.toFixed(1)} / ${section.weight}`;
        doc.text(subtotalText, pageW - 18 - doc.getTextWidth(subtotalText), y + 5);
        y += 11;

        totalSelfScore += sectionSelfTotal;
        totalHrScore += sectionHrTotal;
        totalMaxWeight += section.weight;
      }

      // Reviewer and admin comments for this section
      addSectionComments('Reviewer Comments:', questions, hrResponses);
      addSectionComments('Admin Comments:', questions, adminResponses);
      y += 4;
    }
  }

  // ── OVERALL APPRAISAL TOTAL ──
  if (totalMaxWeight > 0) {
    checkSpace(24);
    doc.setFillColor(...PRIMARY);
    doc.roundedRect(14, y, pageW - 28, 20, 3, 3, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Overall Appraisal Score', 20, y + 8);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Self: ${totalSelfScore.toFixed(1)} / ${totalMaxWeight}     Reviewer: ${totalHrScore.toFixed(1)} / ${totalMaxWeight}`,
      20,
      y + 15,
    );
    y += 26;
  }

  // ── MANAGER'S FINAL COMMENT ──
  addFinalComment(
    "Manager's Final Comment",
    appraisal.hrReview?.overall_comment || appraisal.approvalReview?.overall_comment,
  );

  // ── EMPLOYEE COMMENTS ──
  if (appraisal.employeeComments?.comments) {
    checkSpace(30);
    doc.setFillColor(254, 243, 199);
    doc.roundedRect(14, y, pageW - 28, 6, 2, 2, 'F');
    doc.setTextColor(146, 64, 14);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Comments', 18, y + 4.5);
    y += 10;

    doc.setTextColor(...DARK);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const commentLines = doc.splitTextToSize(appraisal.employeeComments.comments, pageW - 40);
    checkSpace(commentLines.length * 4 + 4);
    doc.text(commentLines, 18, y);
    y += commentLines.length * 4 + 2;

    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(`Acknowledged: ${appraisal.employeeComments.accepted ? 'Yes' : 'No'}`, 18, y);
    y += 8;
  }

  // ── ADMIN'S FINAL COMMENT ──
  addFinalComment("Admin's Final Comment", appraisal.adminReview?.comments);

  // ── HR SIGN-OFF DETAILS ──
  if (appraisal.hrSignOff?.comments) {
    checkSpace(30);
    doc.setFillColor(220, 252, 231);
    doc.roundedRect(14, y, pageW - 28, 6, 2, 2, 'F');
    doc.setTextColor(21, 128, 61);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('HR Sign-off Remarks', 18, y + 4.5);
    y += 10;

    doc.setTextColor(...DARK);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const signLines = doc.splitTextToSize(appraisal.hrSignOff.comments, pageW - 40);
    checkSpace(signLines.length * 4 + 4);
    doc.text(signLines, 18, y);
    y += signLines.length * 4 + 8;
  }

  // ── FOOTER on each page ──
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(`Page ${i} of ${totalPages}`, pageW / 2, 290, { align: 'center' });
    doc.text('Confidential — Zenith', 14, 290);
  }

  const fileName = `Appraisal_${appraisal.employeeName.replace(/\s+/g, '_')}_${(appraisal.cycleName || 'Report').replace(/\s+/g, '_')}.pdf`;
  await savePdfDocument(doc, fileName, 'Appraisal Report');
  return fileName;
}
